import explorerTool from "@/tools/explorer";
import archetypesTool from "@/tools/reference/archetypes";
import changelogTool from "@/tools/reference/changelog";
import faqsTool from "@/tools/reference/faqs";
import geocodingTool from "@/tools/reference/geocoding";
import glossaryTool from "@/tools/reference/glossary";
import methodsTool from "@/tools/reference/methods";
import modellingTool from "@/tools/reference/modelling";
import { Tool, ToolsObject } from "@/types/tools";
import { AuthSession } from "@supabase/supabase-js";
import { jwtDecode } from "jwt-decode";
import { getConfig, getKbConfig } from "./utils";

// Tools which will not be subject to database permissions filtering.
const filterExceptedTools = [glossaryTool.keyPrefix, archetypesTool.keyPrefix, methodsTool.keyPrefix, modellingTool.keyPrefix, geocodingTool.keyPrefix, changelogTool.keyPrefix, faqsTool.keyPrefix, explorerTool.keyPrefix];

/**
 * Returns an array of available tools.
 * @returns All tools which are available to be used in this Hub instance.
 */
export const getAllTools = (): Tool[] => {
  const allTools = [...getConfig().featureSet.tools];
  const {
    glossary,
    archetypes,
    method,
    changeLog,
    faqs,
    modelling,
    geocoding,
    dataExplorer
  } = getKbConfig();
  if (glossary.enabled) {
    allTools.push(glossaryTool);
  }
  if (archetypes.enabled) {
    allTools.push(archetypesTool);
  }
  if (method.enabled || method.publicDocsUrl) {
    allTools.push(methodsTool);
  }
  if (geocoding.enabled) {
    allTools.push(geocodingTool);
  }
  if (modelling.enabled) {
    allTools.push(modellingTool);
  }
  if (faqs.enabled) {
    allTools.push(faqsTool);
  }
  if (changeLog.enabled) {
    allTools.push(changelogTool);
  }
  if (dataExplorer.enabled) {
    allTools.push(explorerTool);
  }
  return allTools;
};
export const getAllToolsAsObject = (): ToolsObject => {
  return buildToolsObject(getAllTools());
};

/**
 * Returns an array of available tools allowed for the current user.
 * @returns All tools which are available to be used for the current user.
 */
export const getTools = async (session: AuthSession | null): Promise<Tool[]> => {
  if (!session) return [];
  const decoded = jwtDecode(session.access_token) as AuthSession["user"];
  const toolsEnabled = decoded.user_metadata?.tools_enabled || [];
  const filteredTools = getAllTools().filter(tool => {
    return toolsEnabled.includes(tool.keyPrefix) || filterExceptedTools.includes(tool.keyPrefix);
  });

  // Include all required subtools.
  filteredTools.forEach(tool => {
    if (tool.subTools) {
      filteredTools.push(...tool.subTools);
    }
  });

  // Remove all duplicates.
  return Array.from(new Set(filteredTools));
};
export const getToolsAsObject = async (session: AuthSession | null): Promise<ToolsObject> => {
  return buildToolsObject(await getTools(session));
};
const buildToolsObject = (tools: Tool[]) => {
  return tools.reduce<ToolsObject>((toolsObject, tool) => {
    return {
      ...toolsObject,
      [tool.category]: [...toolsObject[tool.category], tool]
    };
  }, {
    admin: [],
    screening: [],
    assetLevelDeepDive: [],
    coordinateGeneration: [],
    create: [],
    comprehensive: [],
    dataVisualisation: [],
    marketIntelligence: [],
    structural: [],
    reference: []
  });
};